/**
 * `Create new` for an unmatched role holder (f8.3).
 *
 * **Thirty lines, and twenty-eight of them are wiring.** That is the point:
 * the dialog itself is f3.2's, mounted rather than forked, and everything this
 * file does is decide what it opens with and where its submission goes. A
 * second invite form would have been the parallel creation path the feature
 * exists to prevent -- and `ports/user_repository.py` forbids a third create
 * on the server for the same reason.
 *
 * **The submission goes through the RESOLUTION request, not `POST /users`.**
 * The workbook contains no e-mail address: a human types it, and it can be
 * malformed or already taken. Deferring the invite into the commit transaction
 * would surface `EMAIL_ALREADY_EXISTS` as a failed commit that rolled back all
 * 124 nodes, minutes later, in a worker. Sending it with the resolution means
 * the server calls the very same `invite_user` service inside the resolution's
 * own transaction, so a refusal rolls back the account AND the stamp together,
 * and the dialog stays open over the typed values with the message bound to
 * the offending field (design D3).
 *
 * **The role is prefilled to `UNASSIGNED`, read from the live catalogue.**
 * BR-F4.2-4: an imported or invited account lands with zero permissions, and
 * the import never copies, infers or guesses a platform role. The Admin may
 * change it before submitting -- that is an Admin choosing a role on a form,
 * identical to what they could do one click later on the Users screen.
 *
 * The prototype's `Scope` select is **not** built: scope is derived from
 * ownership in f9.1 and is never a field on a user. Nor is its role list,
 * which still names two roles f1.3 never shipped.
 */

import type { LookupValue, RoleHolderDecision } from '@/api/client';
import { useResolveRoleHolder } from '@/api/queries';
import {
  InviteUserDialog,
  type InvitedAccount,
  type InviteSubmitter,
} from '@/users/InviteUserDialog';
import { unassignedRoleId } from '@/users/platformRoles';

export function InviteRoleHolderDialog({
  batchId,
  decision,
  platformRoles,
  onInvited,
  onClose,
}: {
  batchId: string;
  decision: RoleHolderDecision;
  /** f2.2's `PLATFORM_ROLE` catalogue. The `UNASSIGNED` id is read from it. */
  platformRoles: readonly LookupValue[];
  onInvited: (user: InvitedAccount) => void;
  onClose: () => void;
}) {
  const resolve = useResolveRoleHolder(batchId);

  const submitter: InviteSubmitter = {
    submit: (values) =>
      resolve
        .mutateAsync({
          normalized_key: decision.normalized_key,
          kind: 'INVITE',
          invite: values,
        })
        .then((resolved) => resolved.user),
    isPending: resolve.isPending,
    error: resolve.error,
  };

  return (
    <InviteUserDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      defaultValues={{
        // The canonical `First Last` form the SERVER derived with the same
        // function the invite service will run on whatever is submitted, so
        // accepting the prefill creates an account a re-parse would match.
        display_name: decision.suggested_display_name,
        // Left empty deliberately: the workbook has no address to prefill.
        platform_role_id: unassignedRoleId(platformRoles),
      }}
      roles={platformRoles}
      submitter={submitter}
      onInvited={onInvited}
    />
  );
}
